import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutGrid, RefreshCw, Search, Users, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { StatTile } from '@/components/ui/StatTile';
import { Loader } from '@/components/ui/Loader';
import { fetchQueueStatus } from '@/services/api';
import type { QueueAgent, QueueStatus } from '@/types';
import { showErrorToast } from '@/utils/toast';
import {
  CALL_STATE,
  STATE_META,
  STATE_ORDER,
  agentKey,
  agentLabel,
  byText,
  countStates,
  membershipState,
  type MembershipState,
} from './queueAgentState';

type StatusFilter = 'all' | MembershipState;
type ViewMode = 'queues' | 'agents';

interface Membership {
  queue: QueueStatus;
  agent: QueueAgent;
  state: MembershipState;
}

const REFRESH_MS = 10000;

export function QueueAgentBoard() {
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [view, setView] = useState<ViewMode>('queues');
  const loadInFlight = useRef(false);

  const load = useCallback(async () => {
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    setRefreshing(true);
    try {
      const data = await fetchQueueStatus();
      setQueues(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading queue agents:', error);
      showErrorToast(
        error,
        'Failed to load queue agents',
        'queue-agent-board-load-error'
      );
    } finally {
      loadInFlight.current = false;
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!autoRefresh) return;
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, load]);

  // Search matches queue name/number or agent name/extension. A queue match
  // keeps all of that queue's members; otherwise only matching agents stay.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result: Membership[] = [];
    queues.forEach((queue) => {
      const queueMatches =
        !q ||
        queue.queue_name?.toLowerCase().includes(q) ||
        !!queue.queue_number?.toLowerCase().includes(q);
      queue.agents.forEach((agent) => {
        const agentMatches =
          queueMatches ||
          agent.agent_name?.toLowerCase().includes(q) ||
          agent.agent_num?.toLowerCase().includes(q);
        if (agentMatches) result.push({ queue, agent, state: membershipState(agent) });
      });
    });
    return result;
  }, [queues, search]);

  const counts = useMemo(() => countStates(searched), [searched]);
  const visible = useMemo(
    () =>
      statusFilter === 'all'
        ? searched
        : searched.filter((m) => m.state === statusFilter),
    [searched, statusFilter]
  );

  const uniqueAgents = useMemo(
    () => new Set(searched.map((m) => agentKey(m.agent))).size,
    [searched]
  );

  const queueGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const map = new Map<string, { queue: QueueStatus; members: Membership[] }>();
    // Show queues with no members only when nothing narrows the list.
    if (!q && statusFilter === 'all') {
      queues.forEach((queue) => map.set(queue.queue_id, { queue, members: [] }));
    }
    visible.forEach((m) => {
      const group = map.get(m.queue.queue_id) ?? { queue: m.queue, members: [] };
      group.members.push(m);
      map.set(m.queue.queue_id, group);
    });
    return Array.from(map.values()).sort((a, b) =>
      byText(a.queue.queue_name || '', b.queue.queue_name || '')
    );
  }, [queues, visible, search, statusFilter]);

  const agentGroups = useMemo(() => {
    const map = new Map<string, { agent: QueueAgent; members: Membership[] }>();
    visible.forEach((m) => {
      const key = agentKey(m.agent);
      const group = map.get(key) ?? { agent: m.agent, members: [] };
      group.members.push(m);
      map.set(key, group);
    });
    return Array.from(map.values()).sort((a, b) =>
      byText(agentLabel(a.agent), agentLabel(b.agent))
    );
  }, [visible]);

  if (loading) {
    return (
      <Card>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
          Queue Agents
        </h2>
        <Loader text="Loading queue members..." />
      </Card>
    );
  }

  const filterOptions: Array<{ id: StatusFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: searched.length },
    ...STATE_ORDER.map((state) => ({
      id: state,
      label: STATE_META[state].label,
      count: counts[state],
    })),
  ];

  return (
    <Card>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Queue Agents
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Queue membership and who is signed in, paused or logged out
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => load()}
            title="Refresh now"
            className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : undefined} />
          </button>
          <button
            type="button"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full ${
              autoRefresh
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                : 'bg-panel text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </button>
        </div>
      </div>

      {/* Summary tiles double as status filters */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
        {STATE_ORDER.map((state) => {
          const meta = STATE_META[state];
          const active = statusFilter === state;
          return (
            <StatTile
              key={state}
              size="md"
              surface="inset"
              label={meta.label}
              icon={null}
              dotClassName={meta.dot}
              value={counts[state]}
              caption="queue memberships"
              active={active}
              onClick={() => setStatusFilter(active ? 'all' : state)}
            />
          );
        })}
        <StatTile
          size="md"
          surface="inset"
          label="Agents"
          icon={Users}
          value={uniqueAgents}
          caption={`across ${new Set(searched.map((m) => m.queue.queue_id)).size} queues`}
        />
      </div>

      {/* Search, status filter and view toggle */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center mb-5">
        <div className="relative flex-1 min-w-0">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search queue name or number, agent or extension…"
            className="w-full pl-9 pr-9 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              title="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filterOptions.map((option) => {
            const active = statusFilter === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setStatusFilter(option.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {option.id !== 'all' && (
                  <span className={`w-2 h-2 rounded-full ${STATE_META[option.id].dot}`} />
                )}
                {option.label}
                <span
                  className={
                    active ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {option.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0 self-start lg:self-auto">
          {(
            [
              { id: 'queues', label: 'By queue', icon: LayoutGrid },
              { id: 'agents', label: 'By agent', icon: Users },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium ${
                view === id
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {queues.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No queues found
        </p>
      ) : view === 'queues' ? (
        queueGroups.length === 0 ? (
          <EmptyResult />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {queueGroups.map(({ queue, members }) => (
              <QueuePanel key={queue.queue_id} queue={queue} members={members} />
            ))}
          </div>
        )
      ) : agentGroups.length === 0 ? (
        <EmptyResult />
      ) : (
        <AgentTable groups={agentGroups} />
      )}
    </Card>
  );
}

function EmptyResult() {
  return (
    <p className="text-center text-gray-500 dark:text-gray-400 py-8">
      No queues or agents match the current search and filter
    </p>
  );
}

function QueuePanel({ queue, members }: { queue: QueueStatus; members: Membership[] }) {
  const queueCounts = countStates(
    queue.agents.map((agent) => ({ queue, agent, state: membershipState(agent) }))
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {queue.queue_name}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {queue.queue_number ? `Queue ${queue.queue_number} · ` : ''}
            {queue.agents.length} member{queue.agents.length === 1 ? '' : 's'} ·{' '}
            {queue.waiting_count} waiting · {queue.active_count} active
          </p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {STATE_ORDER.map((state) => (
            <span
              key={state}
              title={STATE_META[state].label}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATE_META[state].chip}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${STATE_META[state].dot}`} />
              {queueCounts[state]}
            </span>
          ))}
        </div>
      </div>
      {members.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">No members</p>
      ) : (
        <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {STATE_ORDER.map((state) => {
            const rows = members
              .filter((m) => m.state === state)
              .sort((a, b) => byText(agentLabel(a.agent), agentLabel(b.agent)));
            if (rows.length === 0) return null;
            return (
              <div key={state} className="px-4 py-2">
                <p
                  className={`text-xs font-semibold uppercase tracking-wide mb-1 ${STATE_META[state].text}`}
                >
                  {STATE_META[state].label} ({rows.length})
                </p>
                <ul>
                  {rows.map(({ agent }) => (
                    <AgentRow
                      key={agent.agent_id || agent.agent_num}
                      agent={agent}
                      state={state}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AgentRow({ agent, state }: { agent: QueueAgent; state: MembershipState }) {
  const call = CALL_STATE[agent.status];
  return (
    <li className="flex items-center justify-between gap-3 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${STATE_META[state].dot}`} />
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {agentLabel(agent)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
          {agent.agent_num}
        </span>
        {agent.agent_type === 'static' && (
          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 shrink-0">
            Static
          </span>
        )}
      </div>
      <span className="text-xs shrink-0 text-right">
        {state === 'paused' ? (
          <span className="text-amber-700 dark:text-amber-400">
            {agent.pause_reason || 'Paused'}
          </span>
        ) : state === 'signed_in' ? (
          <span className={call.className}>{call.label}</span>
        ) : state === 'offline' || state === 'dnd' ? (
          <span className={STATE_META[state].text}>{STATE_META[state].description}</span>
        ) : null}
      </span>
    </li>
  );
}

function AgentTable({
  groups,
}: {
  groups: Array<{ agent: QueueAgent; members: Membership[] }>;
}) {
  return (
    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-700/50 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-4 py-2 font-medium">Agent</th>
            <th className="px-4 py-2 font-medium">Call state</th>
            {STATE_ORDER.map((state) => (
              <th key={state} className="px-4 py-2 font-medium">
                {STATE_META[state].label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {groups.map(({ agent, members }) => {
            const call = CALL_STATE[agent.status];
            return (
              <tr key={agentKey(agent)} className="align-top">
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {agentLabel(agent)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ext {agent.agent_num}
                  </p>
                </td>
                <td className={`px-4 py-2.5 whitespace-nowrap text-xs ${call.className}`}>
                  {call.label}
                </td>
                {STATE_ORDER.map((state) => {
                  const inState = members
                    .filter((m) => m.state === state)
                    .sort((a, b) =>
                      byText(a.queue.queue_name || '', b.queue.queue_name || '')
                    );
                  return (
                    <td key={state} className="px-4 py-2.5">
                      {inState.length === 0 ? (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {inState.map(({ queue, agent: member }) => (
                            <span
                              key={queue.queue_id}
                              title={
                                state === 'paused' && member.pause_reason
                                  ? `Paused: ${member.pause_reason}`
                                  : undefined
                              }
                              className={`px-2 py-0.5 rounded-full text-xs ${STATE_META[state].chip}`}
                            >
                              {queue.queue_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
